import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider, MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Observable } from 'rxjs';
import { FormulaVerificationResult } from 'src/app/shared/models/test.model';
import { TestService } from 'src/app/shared/test.service';
import { LimitGuideDialogComponent } from '../limit-guide-dialog/limit-guide-dialog.component';



interface Test {
  TestId: number;
  Name: string;
  LimitOK: string;
  LimitGood: string;
  LimitVeryGood: string;
  LimitExcellent: string;
  Formula: string;
  Unit: string;
  SortOrder: number;
  UseFormula: boolean;
}


@Component({
  selector: 'app-edit-limit-dialog',
  imports: [MatDialogModule, MatFormFieldModule, ReactiveFormsModule, MatLabel, MatInputModule, ReactiveFormsModule, MatSlideToggleModule,
    MatButtonModule, MatSelectModule, MatLabel, MatDividerModule, MatAutocompleteModule, MatIconModule, MatButtonModule,
    AsyncPipe],
  templateUrl: './edit-limit-dialog.component.html',
  styleUrl: './edit-limit-dialog.component.css'
})

export class EditLimitDialogComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  data = inject(MAT_DIALOG_DATA);
  private testService = inject(TestService);  
  fb = inject(FormBuilder);
  formTest: FormGroup;
  limit = this.data.Limit;
  allTests$;
  shouldShowAutocomplete = false; // Add this flag
  formulaIsValidated = signal<boolean | null>(true);
  originalFormula = this.data.Limit?.Formula ?? '';
  originalUseFormula = this.data.Limit?.UseFormula ?? false;
  isFormulaChangedSinceLastVerify = signal<boolean>(false);

  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  @ViewChild('formulaInput') formulaInput!: ElementRef<HTMLInputElement>;
  cursorPosition: number;
  FormulaErrors: string[] = null;
  FormulaHasErrors: boolean;
  DynamicLimitsEnabled: boolean = true;


  constructor(public dialogRef: MatDialogRef<EditLimitDialogComponent>) {

  }


  ngOnInit(): void {
    // Initialize the component

    this.allTests$ = this.testService.getAvailableTestsForProfile(this.data.ProfileId);
      

    //console.log(this.data);
    this.formTest = this.fb.group({
      ProfileId: [this.data.ProfileId],
      TestId: [this.data.Test.TestId],
      Name: [this.data.Test.Name],
      LimitOK: [this.data.Limit.LimitOK, [Validators.required]],
      LimitGood: [this.data.Limit.LimitGood],
      LimitVeryGood: [this.data.Limit.LimitVeryGood],
      LimitExcellent: [this.data.Limit.LimitExcellent],
      UseFormula: [this.data.Limit.UseFormula],
      Formula: [{ value: this.data.Limit?.Formula ?? '', disabled: !this.data.Limit?.UseFormula }],
      Unit: [this.data.Unit],
      OrderInProfile: [this.data.Test.OrderInProfile],
      IncludeInTotalRanking: [this.data.Test.IncludeInTotalRanking],
      LimitMultiplierTestId:  [{ value: this.data.Limit?.LimitMultiplierTestId ?? '', disabled: this.data.Limit?.UseFormula }],
      LimitMultiplierTestName:  [this.data.Limit.LimitMultiplierTestName],
      ResultUnit: [this.data.Test.ResultUnit]
      
    });

    console.log(this.data.Test.ResultType);
    //Check if dynamic limits will be enabled
    if (this.data.Test.ResultType >= 30) {
      this.DynamicLimitsEnabled = false;
    }

     this.formTest.get('UseFormula')?.valueChanges.subscribe((useFormula: boolean) => {
      const formulaControl = this.formTest.get('Formula');
      const multiplierControl = this.formTest.get('LimitMultiplierTestId');

      if (useFormula) {
        formulaControl?.enable();
        multiplierControl?.disable();
      } else {
        formulaControl?.disable();
        multiplierControl?.enable();
      }
    });
  }



  save() {
    //console.log(this.formTest.value);
    var limits = [this.formTest.value]; // Wrap in array
    this.testService.updateLimits(limits).subscribe(r => {
      console.log(r);
      //Close dialog
      this.dialogRef.close(true);

    });
  }

  showLimitGuide() {
    const dialogRef2 = this.dialog.open(LimitGuideDialogComponent, {
      width: '600px',

    });

    dialogRef2.afterClosed().subscribe(result => {
      if (result) {
        // Handle any result from the guide dialog if needed
        console.log('Guide dialog closed with result:', result);
      }
    });
  }


  verifyFormula() {
    const formula = this.formTest.get('Formula')?.value;
    var formulaVerify = new FormulaVerificationResult();
    formulaVerify.Formula = formula;
    formulaVerify.ProfileId = this.data.ProfileId;
    this.testService.verifyFLimitFormula(formulaVerify).subscribe(result => {
      console.log('Formula verification result:', result);
      this.FormulaHasErrors = result.IsValid ? false : true;
      this.FormulaErrors = result.Errors;
      if (result.IsValid) {
        this.formulaIsValidated.set(true);
        this.isFormulaChangedSinceLastVerify.set(false);
        // Handle the result (e.g., show a m  essage to the user)
      } else {
        this.formulaIsValidated.set(false);

      }
    });

  }
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.cursorPosition = input.selectionStart || 0;
    this.isFormulaChangedSinceLastVerify.set(true);


    const textBeforeCursor = value.substring(0, this.cursorPosition);
    const lastBracketIndex = textBeforeCursor.lastIndexOf('[');

    // Check if we should show autocomplete
    if (lastBracketIndex !== -1) {
      const textAfterBracket = textBeforeCursor.substring(lastBracketIndex + 1);
      const hasClosingBracket = textAfterBracket.includes(']');

      // Show only if there's an unclosed bracket before cursor
      this.shouldShowAutocomplete = !hasClosingBracket;

      if (this.shouldShowAutocomplete) {
        setTimeout(() => this.autoTrigger.openPanel(), 0);
      } else {
        this.autoTrigger.closePanel();
      }
    } else {
      this.shouldShowAutocomplete = false;
      this.autoTrigger.closePanel();
    }
  }

  onFocus() {
    this.shouldShowAutocomplete = false;
    // Close the panel when input gets focus to prevent auto-opening
    setTimeout(() => {
      this.autoTrigger?.closePanel();
    }, 0);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    console.log('Option selected event:', event);
    /*  const selected = event.option.value; */
    let currentValue = this.formTest.controls['Formula'].value;

    //   console.log(`Selected option: ${selected}`);
    console.log(currentValue);

    const selected = event.option.value;
    //const input = this.formulaInput.nativeElement;
    //var currentValue = input.value;

    console.log(`Selected option: ${selected}`);
    console.log(`Current value from input: ${currentValue}`);
    console.log(`Cursor position: ${this.cursorPosition}`);

    // Find last '[' and everything after it
    const lastBracketIndex = currentValue.lastIndexOf('[');

    if (lastBracketIndex !== -1) {
      // Keep text before '[', insert value, and add closing ']'
      const before = currentValue.substring(0, lastBracketIndex + 1);
      const after = currentValue.substring(lastBracketIndex + 1);

      // Replace everything after '[' until ']' (if present)
      const closingIndex = after.indexOf(']');
      const textAfterBracket = closingIndex >= 0 ? after.substring(closingIndex + 1) : '';

      currentValue = `${before}${selected}]${textAfterBracket}`;
      this.formTest.controls['Formula'].setValue(currentValue);
    }

    // Close panel manually
    this.autoTrigger.closePanel();
  }

 }




